// framework imports - 1st party
import React, { useEffect, useState } from "react";

// lib imports - 3rd party
import Modal from "react-modal";
import Select from "react-select";

// app imports
import * as api from "../../api";
import * as notify from "../../notify";
import useDebounce from "../../useDebounce";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const SELECT_ALL_VALUE = "__select_all__";
const POLICY_CHIP_AGENCY_LOCATION_ALL = "POLICY_CHIP_AGENCY_LOCATION_ALL";
const POLICY_PLC_ALL = "POLICY_PLC_ALL";
const ROLE_VIRTUAL_TERMINAL = "ROLE_VIRTUAL_TERMINAL";
const PLC_SEARCH_DEBOUNCE_MS = 900;

const VtUserManagementAddUser = (props) => {
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loadingProfile, setLoadingProfile] = useState(false);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [selectedPlcs, setSelectedPlcs] = useState([]);
  const [selectedLocations, setSelectedLocations] = useState([]);

  const [plcOptions, setPlcOptions] = useState([]);
  const [locationOptions, setLocationOptions] = useState([]);
  const [profilePolicies, setProfilePolicies] = useState([]);
  const [plcSearchInput, setPlcSearchInput] = useState("");
  const [searchingPlcs, setSearchingPlcs] = useState(false);
  const [plcSearchCompleted, setPlcSearchCompleted] = useState(false);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const debouncedPlcSearchInput = useDebounce(
    plcSearchInput,
    PLC_SEARCH_DEBOUNCE_MS
  );

  const resetForm = () => {
    setFirstName("");
    setLastName("");
    setEmail("");
    setSelectedPlcs([]);
    setSelectedLocations([]);
    setPlcOptions([]);
    setLocationOptions([]);
    setProfilePolicies([]);
    setPlcSearchInput("");
    setPlcSearchCompleted(false);
    setLoadingLocations(false);
  };

  const handleOpenModal = () => {
    resetForm();
    setShowModal(true);
    setLoadingProfile(true);

    Promise.all([
      api.getVtUserManagementProfile(),
      api.getVtUserManagementRoles(),
    ]).then(([response, rolesResponse]) => {
      const plcs = response && response.plcs ? response.plcs : [];
      const locations =
        response && response.locations ? response.locations : [];
      const roles = Array.isArray(rolesResponse) ? rolesResponse : [];
      const profileRole = response && response.role ? response.role : "";
      const currentRole = roles.find((role) => role.id === profileRole);
      const policies =
        currentRole && currentRole.policies
          ? currentRole.policies
          : response && response.policies
          ? response.policies
          : [];
      const canSearchAllPlcs = policies.includes(POLICY_PLC_ALL);

      setProfilePolicies(policies);
      setPlcOptions(
        canSearchAllPlcs
          ? []
          : plcs.map((plc) => ({
              value: plc.id,
              label: plc.displayName
                ? `${plc.id} - ${plc.displayName}`
                : plc.id,
            }))
      );
      setLocationOptions(
        locations.map((location) => ({
          value: location.id,
          label: location.name
            ? `${location.name} (${location.id})`
            : `${location.id}`,
        }))
      );

      if (response && response.error) {
        notify.error("Failed to load assignable PLCs and locations");
      }
      if (rolesResponse && rolesResponse.error) {
        notify.error("Failed to load current user roles");
      }
      setLoadingProfile(false);
    });
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  const hasPlcAllPolicy = profilePolicies.includes(POLICY_PLC_ALL);
  const hasChipLocationAllPolicy = profilePolicies.includes(
    POLICY_CHIP_AGENCY_LOCATION_ALL
  );
  const plcSelectOptions = hasPlcAllPolicy
    ? plcOptions
    : plcOptions.length
    ? [{ value: SELECT_ALL_VALUE, label: "Select All PLCs" }, ...plcOptions]
    : [];
  const locationsPending =
    hasPlcAllPolicy && selectedPlcs.length > 0 && loadingLocations;
  const showLocations = hasPlcAllPolicy
    ? selectedPlcs.length > 0 && !hasChipLocationAllPolicy
    : locationOptions.length > 0 && !hasChipLocationAllPolicy;
  const locationSelectOptions = locationOptions.length
    ? [
        { value: SELECT_ALL_VALUE, label: "Select All Locations" },
        ...locationOptions,
      ]
    : [];

  const getPlcNoOptionsMessage = () => {
    if (!hasPlcAllPolicy) {
      return "No PLCs available";
    }

    if (searchingPlcs) {
      return "Searching PLCs...";
    }

    if (debouncedPlcSearchInput.trim().length >= 2 && plcSearchCompleted) {
      return "No PLCs found";
    }

    return "Type at least 2 characters to search PLCs";
  };

  const handlePlcChange = (value) => {
    const selected = value || [];
    if (selected.some((option) => option.value === SELECT_ALL_VALUE)) {
      setSelectedPlcs(plcOptions);
    } else {
      setSelectedPlcs(selected);
    }
  };

  const handlePlcSearchInputChange = (value, actionMeta) => {
    if (!hasPlcAllPolicy || actionMeta.action !== "input-change") {
      return value;
    }

    setPlcSearchInput(value);
    setPlcSearchCompleted(false);
    return value;
  };

  useEffect(() => {
    if (!hasPlcAllPolicy) {
      return;
    }

    const trimmed = debouncedPlcSearchInput.trim();
    if (!trimmed || trimmed.length < 2) {
      setPlcOptions([]);
      setSearchingPlcs(false);
      setPlcSearchCompleted(false);
      return;
    }

    let cancelled = false;
    setSearchingPlcs(true);
    setPlcSearchCompleted(false);
    api.searchVtUserManagementPlcs(trimmed).then((response) => {
      if (cancelled) {
        return;
      }

      const searchResults =
        response && response.searchResult ? response.searchResult : [];
      setPlcOptions(
        searchResults.map((plc) => ({
          value: plc.id,
          label: `${plc.id} - ${plc.name} - ${plc.account_type} - (${plc.state})`,
        }))
      );
      if (response && response.error) {
        notify.error("Failed to search PLCs");
      }
      setSearchingPlcs(false);
      setPlcSearchCompleted(true);
    });

    return () => {
      cancelled = true;
    };
  }, [debouncedPlcSearchInput, hasPlcAllPolicy]);

  useEffect(() => {
    if (!hasPlcAllPolicy) {
      return;
    }

    if (!selectedPlcs.length) {
      setLocationOptions([]);
      setSelectedLocations([]);
      return;
    }

    let cancelled = false;
    setLoadingLocations(true);
    const plcIds = selectedPlcs.map((option) => option.value);
    api.getVtUserManagementLocationsByPlcs(plcIds).then((response) => {
      if (cancelled) {
        return;
      }

      const locations =
        response && response.locations ? response.locations : [];
      const options = locations.map((location) => ({
        value: location.id,
        label: location.name
          ? `${location.name} (${location.id})`
          : `${location.id}`,
      }));
      setLocationOptions(options);
      setSelectedLocations((current) =>
        current.filter((selected) =>
          options.some(
            (option) => String(option.value) === String(selected.value)
          )
        )
      );
      if (response && response.error) {
        notify.error("Failed to load locations for selected PLCs");
      }
      setLoadingLocations(false);
    });

    return () => {
      cancelled = true;
    };
  }, [selectedPlcs, hasPlcAllPolicy]);

  const handleLocationChange = (value) => {
    const selected = value || [];
    if (selected.some((option) => option.value === SELECT_ALL_VALUE)) {
      setSelectedLocations(locationOptions);
    } else {
      setSelectedLocations(selected);
    }
  };

  const isValid =
    firstName.trim() &&
    lastName.trim() &&
    EMAIL_PATTERN.test(email.trim()) &&
    selectedPlcs.length > 0 &&
    !locationsPending;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!isValid || submitting) {
      return;
    }

    setSubmitting(true);
    const payload = {
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      email: email.trim(),
      plcs: selectedPlcs.map((option) => option.value),
      locations: showLocations
        ? selectedLocations.map((option) => option.value)
        : [],
      role: ROLE_VIRTUAL_TERMINAL,
      policies: ROLE_VIRTUAL_TERMINAL,
    };

    api.createVtUserManagementUser(payload).then((response) => {
      setSubmitting(false);
      const data = response && response.data;
      if (data && data.User) {
        notify.success("User created", true);
        handleCloseModal();
        resetForm();
        if (props.onCreated) {
          props.onCreated();
        }
      } else {
        notify.error((data && data.error) || "Failed to create user");
      }
    });
  };

  return (
    <div className="vt-add-user">
      <button
        className="proview-button report no-margin-left"
        onClick={handleOpenModal}
        type="button"
      >
        Add User
      </button>

      <Modal
        isOpen={showModal}
        shouldCloseOnOverlayClick={true}
        onRequestClose={handleCloseModal}
        className="Modal add-user"
        overlayClassName="Overlay deny"
        ariaHideApp={false}
      >
        <h3>Add User</h3>
        <form name="add-user" onSubmit={handleSubmit}>
          <div className="add-user-form">
            <label htmlFor="add-user-first-name">First Name</label>
            <input
              id="add-user-first-name"
              className="proview-input long"
              placeholder="First Name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
            />
          </div>
          <div className="add-user-form">
            <label htmlFor="add-user-last-name">Last Name</label>
            <input
              id="add-user-last-name"
              className="proview-input long"
              placeholder="Last Name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
            />
          </div>
          <div className="add-user-form">
            <label htmlFor="add-user-email">Email</label>
            <input
              id="add-user-email"
              className="proview-input long"
              placeholder="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div className="add-user-form">
            <label htmlFor="add-user-plcs">
              {hasPlcAllPolicy ? "Search PLCs" : "PLCs"}
            </label>
            <Select
              inputId="add-user-plcs"
              isMulti
              isLoading={loadingProfile || searchingPlcs}
              options={plcSelectOptions}
              value={selectedPlcs}
              onChange={handlePlcChange}
              onInputChange={handlePlcSearchInputChange}
              openMenuOnClick={!hasPlcAllPolicy}
              noOptionsMessage={getPlcNoOptionsMessage}
              classNamePrefix="select"
              placeholder={hasPlcAllPolicy ? "Search PLCs" : "Select PLCs"}
            />
          </div>
          {showLocations && (
            <div className="add-user-form">
              <label htmlFor="add-user-locations">Locations</label>
              <Select
                inputId="add-user-locations"
                isMulti
                isLoading={loadingProfile || loadingLocations}
                options={locationSelectOptions}
                value={selectedLocations}
                onChange={handleLocationChange}
                classNamePrefix="select"
                placeholder="Select Locations"
              />
            </div>
          )}
          <div className="add-user-actions">
            <button
              className="proview-button cancel left"
              onClick={handleCloseModal}
              type="button"
            >
              Cancel
            </button>
            <button
              className="proview-button right"
              type="submit"
              disabled={!isValid || submitting}
            >
              {submitting
                ? "Adding..."
                : locationsPending
                ? "Loading locations..."
                : "Add"}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default VtUserManagementAddUser;
